import React, { useEffect, useRef, useState } from 'react'

import { get } from '@js/common/api'
import { getUrl } from '@js/common/getUrlByDomain'
import User from '@js/common/user'
import { isHouseRelatedType } from '@js/common/utils'

import { PredictedConversionValue } from '@f/applications/QuizApp/QuizApp'

import { composeLeadAssignmentRequestData } from '@f-fertighaus/applications/RequestWizard/partials/utils'
import { MAX_VENDORS_TO_REQUEST } from '@f-fertighaus/applications/RequestWizard/partials/wizardConstants'

import { Data } from './dataType'
import FinancingConsultationInterest from './SendRequest/FinancingConsultationInterest'
import LocationChange from './SendRequest/LocationChange'
import LocationError from './SendRequest/LocationError'
import LocationMismatch from './SendRequest/LocationMismatch'
import LocationOK from './SendRequest/LocationOK'
import RequestSuccess from './SendRequest/RequestSuccess'

interface Vendor {
  nid: string
  isSelected: boolean
}

interface Props {
  data: Data
  requestType: string
  actions: {
    postRequest: (data?: Data) => void
    subStepRedirect: () => void
    displayLocationChangeScreen: () => void
    handleVendorSelection: (vendorNid: string) => void
    updateProfile: (payload: any, email: string) => void
  }
}

interface StepProps extends Props {
  hardRequestVendors: Vendor[]
}

interface State {
  loading: boolean
  step: React.ComponentType<StepProps> | null
  hardRequestVendors: Vendor[]
  hardRequestDisplayed: boolean
  allowRequestingVendor: boolean
}

const SendRequest: React.FC<Props> = props => {
  const [state, setState] = useState<State>({
    loading: true,
    step: null,
    hardRequestVendors: [],
    hardRequestDisplayed: false,
    allowRequestingVendor:
      User.getRequestedVendors().length <= MAX_VENDORS_TO_REQUEST
  })
  const conversionCallInProgress = useRef(false)

  const handleVendorSelection = (vendorNid: string) => {
    setState(prevState => {
      const hardRequestVendors = prevState.hardRequestVendors.map(vendor =>
        vendor.nid === vendorNid
          ? { ...vendor, isSelected: !vendor.isSelected }
          : vendor
      )

      return {
        ...prevState,
        hardRequestVendors
      }
    })
  }

  const displayLocationChangeScreen = () => {
    setState({
      ...state,
      loading: false,
      step: LocationChange,
      hardRequestVendors: [],
      hardRequestDisplayed: false
    })
  }

  const changeStep = (
    step: React.ComponentType<StepProps>,
    data?: Partial<State>
  ) => {
    setState(prevState => ({
      ...prevState,
      loading: false,
      step,
      ...data
    }))
  }

  const defaultNextStep = !/outfitter|favorite/.test(props.requestType)
    ? FinancingConsultationInterest
    : RequestSuccess

  const subStepRedirect = () => {
    const { postcode } = props.data.location
    const { selected_instances } = props.data
    const { hardRequestDisplayed, hardRequestVendors } = state

    const vendorMatchesDeliveryRegion =
      postcode &&
      selected_instances?.vendors?.[0]?.delivery_regions.includes(postcode)

    setState(prevState => ({ ...prevState, loading: true }))

    const EXCLUDED_REQUEST_TYPES_LOCATION_MATCH_REGEX = /outfitter|mass|catalog|favorite/

    if (!EXCLUDED_REQUEST_TYPES_LOCATION_MATCH_REGEX.test(props.requestType)) {
      if (!postcode) {
        return changeStep(LocationError)
      }
      if (
        hardRequestDisplayed &&
        hardRequestVendors.find(vendor => vendor.isSelected)
      ) {
        return handleHardRequest()
      }

      if (postcode && !vendorMatchesDeliveryRegion) {
        return handleVendorMismatch()
      }

      if (postcode && vendorMatchesDeliveryRegion) {
        return handleVendorMatch()
      }
    } else {
      return changeStep(defaultNextStep)
    }

    return null
  }

  const handleHardRequest = () => {
    const { hardRequestVendors } = state
    window._wt({
      category: 'form',
      action: 'submit',
      location: 'modal-hard-request',
      values: hardRequestVendors
        .filter(vendor => vendor.isSelected)
        .map(vendor => ({
          type: 'vendor_nid',
          value: vendor.nid
        })),
      ...props.data.trackingInfo
    })
    props.actions.postRequest({
      hardRequest: {
        vendors: hardRequestVendors.filter(vendor => vendor.isSelected)
      }
    })
    return changeStep(defaultNextStep)
  }

  const handleVendorMismatch = () => {
    const { allowRequestingVendor } = state
    if (allowRequestingVendor && props.data.selected_instances.houses) {
      return fetchVendors().then(
        vendors => {
          changeStep(LocationMismatch, {
            hardRequestVendors: vendors,
            hardRequestDisplayed: true
          })
        },
        () => changeStep(LocationMismatch)
      )
    }
    return changeStep(LocationMismatch)
  }

  const handleVendorMatch = () => {
    const { allowRequestingVendor, hardRequestDisplayed } = state
    if (allowRequestingVendor && props.data.selected_instances.houses) {
      return fetchVendors().then(
        vendors => {
          if (!hardRequestDisplayed && vendors.length > 0) {
            return changeStep(LocationOK, {
              hardRequestVendors: vendors,
              hardRequestDisplayed: true
            })
          }
          return changeStep(defaultNextStep)
        },
        () => {
          return changeStep(defaultNextStep)
        }
      )
    }

    return changeStep(defaultNextStep)
  }

  const fetchVendors = (): Promise<Vendor[]> => {
    return new Promise((resolveFetchVendors, rejectFetchVendors) => {
      const currentHouse = props.data.selected_instances.houses?.[0] || {}

      const REJECT_TIMEOUT = 8000
      setTimeout(rejectFetchVendors, REJECT_TIMEOUT)

      const leadAssignmentRequestData = composeLeadAssignmentRequestData(
        currentHouse,
        props.data.location,
        props.data.user
      )

      get(getUrl('dataServiceLeadAssignment')(), leadAssignmentRequestData)
        .then(async response => {
          const contactedVendors = [
            ...props.data.selected_instances.vendors.map(vendor => vendor.nid),
            ...User.getRequestedVendors()
          ]
          const vendorNids = response[0].recommended_vendor_set
            .map((vendor: Vendor) => vendor.nid)
            .filter(function filterOutContactedVendors(partnerId: number) {
              return !contactedVendors.includes(partnerId)
            })
            .slice(0, MAX_VENDORS_TO_REQUEST)

          if (!vendorNids.length) {
            rejectFetchVendors()
          }
          const vendors = await get('vendors', {
            nid__in: vendorNids.join(',')
          })

          if (!vendors.results.length) {
            rejectFetchVendors()
          }
          resolveFetchVendors(
            vendors.results.map((vendor: Vendor) => {
              return {
                ...vendor,
                isSelected: true
              }
            })
          )
        })
        .catch(() => {
          rejectFetchVendors()
        })
    })
  }

  useEffect(() => {
    subStepRedirect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    async function getConversionValue() {
      const defaultBudget = 350_000
      const houseInstance = props.data.selected_instances.houses?.[0]
      try {
        const response: PredictedConversionValue = await get(
          'data-services/predicted-prospect-value',
          {
            budget: houseInstance?.price || defaultBudget,
            property: props.data.user.property,
            house_buyer_stage: props.data.user.house_buyer_stage,
            urgency: props.data.user.construction_start,
            target_usecase: 'core'
          }
        )

        if (response.prospect_track) {
          _wt({
            category: 'api',
            action: 'success',
            location: `house-wizard-cv-transition`,
            values: [
              { type: 'conversion_value', value: response.conversion_value }
            ],
            indicates: ['user-input'],
            flow_information: { ...props.data.trackingInfo.flow_information }
          })
          return response
        }
        throw new Error('no-response')
      } catch (error) {
        _wt({
          category: 'api',
          action: 'fail',
          location: `house-wizard-cv-transition`,
          values: [
            {
              type: 'predicted-prospect-value',
              value: error?.message === 'no-response' ? 'no_response' : 'error'
            }
          ],
          flow_information: { ...props.data.trackingInfo.flow_information }
        })
        return error
      }
    }

    if (
      state.loading &&
      !conversionCallInProgress.current &&
      isHouseRelatedType(props.requestType)
    ) {
      conversionCallInProgress.current = true
      getConversionValue().then(r => {
        props.actions.updateProfile(
          {
            contract_probability_base: r.contract_probability,
            prospect_track: r.prospect_track || 'handover'
          },
          props.data.user.email || ''
        )
      })
    }
  }, [
    state.loading,
    props.data.user,
    props.requestType,
    props.data.selected_instances.houses,
    props.data.trackingInfo.flow_information,
    props.actions
  ])

  if (state.loading) {
    return (
      <div className='loading-indicator' style={{ transform: 'scale(0.5)' }} />
    )
  }

  const actions = {
    ...props.actions,
    subStepRedirect,
    displayLocationChangeScreen,
    handleVendorSelection
  }

  if (!state.step) {
    return null
  }

  const Step = state.step

  return (
    <Step
      {...props}
      hardRequestVendors={state.hardRequestVendors}
      actions={actions}
    />
  )
}

export default SendRequest
